/**
 * SVG コンポーネント（DOM非依存の文字列生成）。
 * ハンドオフ採用案の 3a/3b の図版を忠実に再現：
 *  - 日本地図（射場ピン＋打ち上げ後の上昇アーク）
 *  - ISS通過経路図（イラスト・具体的なパス時刻は捏造しない）
 *  - 投入軌道の模式図
 * すべて viewBox 座標系で描き、CSS で幅に追従させる。
 */
import { JAPAN_MAP_PATH } from '../data/japan-map-path';

/** 射場ピンの位置（338×304 の地図SVG座標系。採用案の固定値）。 */
export const PIN_XY: Record<string, [number, number]> = {
  tanegashima: [91.6, 276.9],
  uchinoura: [93.1, 264.0],
  spaceport_kii: [154.6, 229.7],
  hokkaido_spaceport: [254.8, 79.3],
};

/** 種子島と内之浦は近接するため、内之浦のバッジだけ引き出し線で右にずらす（衝突回避）。 */
const BADGE_OFFSET: Record<string, [number, number]> = {
  uchinoura: [132, 254],
};

export interface MapPin {
  id: string;
  num: number;
  selected: boolean;
  isNext: boolean;
}

/** 日本地図＋射場ピン＋上昇アーク。ピンは data-action=select-site でクリック可。 */
export function japanMapSvg(pins: MapPin[]): string {
  const arc = `
    <path d="M91.6,277 C162,266 264,214 312,124" fill="none"
      stroke="rgba(28,71,196,.42)" stroke-width="1.4" stroke-dasharray="4 4" />
    <circle cx="312" cy="124" r="3.6" fill="#1c47c4" />
    <circle cx="312" cy="124" r="9.5" fill="none" stroke="#3a7bff" stroke-width="1" />
    <text x="306" y="112" text-anchor="end" font-family="'Space Mono',monospace" font-size="10"
      letter-spacing=".08em" fill="#5c79ad">LEO 628km</text>
    <text x="176" y="256" font-family="'Space Mono',monospace" font-size="10"
      letter-spacing=".14em" fill="#7085a8">ASCENT · SE</text>`;

  const pinEls = pins
    .map((p) => {
      const xy = PIN_XY[p.id];
      if (!xy) return '';
      const [tx, ty] = xy;
      const [bx, by] = BADGE_OFFSET[p.id] ?? xy;
      const offset = BADGE_OFFSET[p.id] != null;
      const filled = p.selected || p.isNext;
      const badgeFill = filled ? '#3a7bff' : '#ffffff';
      const stroke = '#3a7bff';
      const textFill = filled ? '#ffffff' : '#1c47c4';
      const glow = p.isNext
        ? `<circle cx="${bx}" cy="${by}" r="20" fill="rgba(58,123,255,.16)" />`
        : '';
      const connector = offset
        ? `<line x1="${tx}" y1="${ty}" x2="${bx}" y2="${by}" stroke="rgba(28,71,196,.45)" stroke-width="1" />
           <circle cx="${tx}" cy="${ty}" r="2.6" fill="#1c47c4" />`
        : '';
      return `
      <g class="pin-hit" data-action="select-site" data-site="${p.id}" role="button"
        aria-label="射場 ${p.num} を選ぶ">
        ${connector}${glow}
        <circle class="pin-badge" cx="${bx}" cy="${by}" r="13" fill="${badgeFill}" stroke="${stroke}" stroke-width="1.5" />
        <text x="${bx}" y="${by}" dy=".34em" text-anchor="middle" font-family="'Space Grotesk',sans-serif"
          font-weight="700" font-size="14" fill="${textFill}">${p.num}</text>
      </g>`;
    })
    .join('');

  return `<svg class="map-svg" viewBox="0 0 338 304" role="img"
      aria-label="日本の射場マップ。ピンをクリックすると射場を選べます（詳細は右の射場リスト）。">
    <path d="${JAPAN_MAP_PATH}" fill="rgba(58,123,255,.10)" stroke="rgba(58,123,255,.38)" stroke-width="1"
      stroke-linejoin="round" />
    ${arc}
    ${pinEls}
  </svg>`;
}

/** ISS/衛星の通過経路イメージ（イラスト）。具体的なパス時刻・高度は載せない。 */
export function issDiagramSvg(): string {
  return `<svg class="iss-diagram" viewBox="0 0 190 96" role="img" aria-label="ISSの通過経路のイメージ図">
    <line x1="8" y1="80" x2="182" y2="80" stroke="rgba(28,58,126,.2)" stroke-width="1" />
    <path d="M16,80 C54,4 140,4 174,80" fill="none" stroke="rgba(28,71,196,.42)" stroke-width="1.4" stroke-dasharray="4 4" />
    <circle cx="95" cy="21" r="4" fill="#1c47c4" />
    <circle cx="95" cy="21" r="9.5" fill="none" stroke="#3a7bff" stroke-width="1" />
    <text x="10" y="93" font-family="'Space Mono',monospace" font-size="10" letter-spacing=".12em" fill="#7085a8">SW</text>
    <text x="180" y="93" text-anchor="end" font-family="'Space Mono',monospace" font-size="10" letter-spacing=".12em" fill="#7085a8">NE</text>
  </svg>`;
}

/** 投入軌道の模式図。label は軌道名（LL2の orbit 由来。無ければ「軌道」）。 */
export function orbitDiagramSvg(label: string): string {
  const safe = (label || '軌道').replace(/[<>&]/g, '');
  return `<svg class="orbit-svg" viewBox="0 0 244 96" role="img" aria-label="投入軌道の模式図">
    <defs></defs>
    <circle cx="122" cy="196" r="112" fill="rgba(58,123,255,.1)" stroke="rgba(58,123,255,.28)" stroke-width="1" />
    <ellipse cx="122" cy="196" rx="150" ry="140" fill="none" stroke="rgba(58,123,255,.35)" stroke-width="1" stroke-dasharray="5 5" />
    <path d="M108,86 C120,74 150,64 173,63" fill="none" stroke="rgba(28,71,196,.4)" stroke-width="1.2" stroke-dasharray="3 3" />
    <circle cx="173" cy="63" r="3.6" fill="#1c47c4" />
    <circle cx="173" cy="63" r="8.5" fill="none" stroke="#3a7bff" stroke-width="1" />
    <text x="122" y="30" text-anchor="middle" font-family="'Space Mono',monospace" font-size="11"
      letter-spacing=".1em" fill="#5c79ad">${safe}</text>
  </svg>`;
}
